/**
 * Photo list utilities for filtering and sorting.
 * Extracted from Edit.jsx to reduce cognitive complexity (S3776).
 */

// Import from statusUtils directly to avoid potential circular dependency issues
import { mapFiltersToApiStatus, photoMatchesFilters } from './statusUtils';
import { hiddenStatuses } from './constants';

/**
 * Normalize URL filters by expanding composite filters into their constituents.
 * e.g., "problems" -> ["attention", "error"] so individual filter chips activate.
 * @param {string[]} filters - Raw filter values from URL hash
 * @returns {string[]} Normalized and deduplicated filter values
 */
export function normalizeUrlFilters(filters) {
  const result = [];
  for (const f of filters) {
    if (f === 'problems') {
      result.push('attention', 'error');
    } else {
      result.push(f);
    }
  }
  return [...new Set(result)];
}

/**
 * Default editable statuses shown when "All" filter is selected.
 */
export const EDITABLE_STATUSES = 'skipped,attention,pending,done,exported';

/**
 * Build API params for photo list fetching.
 * @param {Object} options - Options for building params
 * @param {string} options.search - Search query
 * @param {string[]} options.filters - Active filter values
 * @returns {Object} API params object
 */
export function buildPhotoListParams({ search, filters }) {
  const params = {};

  if (search) {
    params.q = search;
  }

  const active = filters || [];
  const hasSpecificFilters = active.length > 0 && !active.includes('all');

  if (hasSpecificFilters) {
    const statusFilters = mapFiltersToApiStatus(active);
    if (statusFilters.length) {
      params.status = statusFilters.join(',');
    }
  } else {
    // "All" or empty filters = only editable statuses
    params.status = EDITABLE_STATUSES;
  }

  return params;
}

/**
 * Filter photos by UI filters (client-side filtering for tagging/review split).
 * @param {Array} photos - List of photos
 * @param {string[]} filters - Active filter values
 * @returns {Array} Filtered photos
 */
export function filterPhotos(photos, filters) {
  // Filter out hidden pipeline statuses
  const visible = photos.filter((p) => !hiddenStatuses.has(p.status));

  const active = filters || [];
  const hasSpecificFilters = active.length > 0 && !active.includes('all');

  if (!hasSpecificFilters) {
    return visible;
  }

  return visible.filter((p) => photoMatchesFilters(p, active));
}

/**
 * Compare function for filename sorting.
 * @param {Object} a - First photo
 * @param {Object} b - Second photo
 * @param {boolean} ascending - Sort direction
 * @returns {number} Comparison result
 */
function compareByFilename(a, b, ascending) {
  const aName = (a.filename || a.image_id || '').toLowerCase();
  const bName = (b.filename || b.image_id || '').toLowerCase();
  const cmp = aName.localeCompare(bName);
  return ascending ? cmp : -cmp;
}

/**
 * Compare function for capture time sorting.
 * @param {Object} a - First photo
 * @param {Object} b - Second photo
 * @param {boolean} ascending - Sort direction (oldest first)
 * @returns {number} Comparison result
 */
function compareByCaptureTime(a, b, ascending) {
  const aTime = new Date(a.capture_time || a.created_at || 0).getTime();
  const bTime = new Date(b.capture_time || b.created_at || 0).getTime();
  return ascending ? aTime - bTime : bTime - aTime;
}

/**
 * Compare function for added time sorting.
 * @param {Object} a - First photo
 * @param {Object} b - Second photo
 * @param {boolean} ascending - Sort direction (oldest first)
 * @returns {number} Comparison result
 */
function compareByAddedTime(a, b, ascending) {
  const aTime = new Date(a.created_at || a.updated_at || 0).getTime();
  const bTime = new Date(b.created_at || b.updated_at || 0).getTime();
  return ascending ? aTime - bTime : bTime - aTime;
}

/**
 * Sort photos by the specified sort order.
 * @param {Array} photos - List of photos to sort
 * @param {string} sortOrder - Sort order key
 * @returns {Array} Sorted photos (new array)
 */
export function sortPhotos(photos, sortOrder) {
  const sorted = [...photos];

  // Lookup table for sort comparators
  const comparators = {
    filename_asc: (a, b) => compareByFilename(a, b, true),
    filename_desc: (a, b) => compareByFilename(a, b, false),
    capture_oldest: (a, b) => compareByCaptureTime(a, b, true),
    capture_newest: (a, b) => compareByCaptureTime(a, b, false),
    oldest: (a, b) => compareByAddedTime(a, b, true),
    newest: (a, b) => compareByAddedTime(a, b, false),
  };

  const comparator = comparators[sortOrder] || comparators.oldest;
  return sorted.sort(comparator);
}

/**
 * Process photo list: filter by status, apply UI filters, and sort.
 * @param {Array} photos - Raw photo list from API
 * @param {string[]} filters - Active filter values
 * @param {string} sortOrder - Sort order key
 * @returns {Array} Processed photo list
 */
export function processPhotoList(photos, filters, sortOrder) {
  const filtered = filterPhotos(photos, filters);
  return sortPhotos(filtered, sortOrder);
}
